import * as React from "react";
import * as Companies from "@/models/companies";

import { IconMail, IconQuestionMark } from "turboui";

import { DropdownMenu, DropdownLinkItem, DropdownSeparator, DropdownActionItem } from "./DropdownMenu";
import { usePaths } from "@/routes/paths";
import { getOverview, EmailOverview } from "@/models/emailManagement";

// Replaces the vendor "Help" nav with a live "Email" control: a glanceable popover of today's send
// totals and a warmup ramp bar per identity, plus a link to the full management page. Company-scoped.
// (Keeps the one useful Help item, keyboard shortcuts, so the rest of the nav wiring is untouched.)
export function EmailDropdown({
  company,
  onOpenKeyboardShortcuts,
}: {
  company: Companies.Company;
  onOpenKeyboardShortcuts: () => void;
}) {
  const paths = usePaths();
  const [overview, setOverview] = React.useState<EmailOverview | null>(null);

  React.useEffect(() => {
    if (!company.permissions?.isAdmin) return;
    getOverview()
      .then(setOverview)
      .catch(() => setOverview(null));
  }, [company.permissions?.isAdmin]);

  const summary = overview?.summary;
  const identities = overview?.identities || [];

  return (
    <DropdownMenu
      testId="email-dropdown"
      name="Email"
      icon={IconMail}
      align="center"
      minWidth={300}
      triggerClassName="hidden lg:flex"
    >
      <div className="px-2 pt-1 pb-1">
        {summary ? (
          <>
            <div className="text-sm font-semibold">
              Today: {summary.sentToday} / {summary.capToday} sent
            </div>
            {summary.backlog > 0 && (
              <div className="text-xs text-content-dimmed mb-2">{summary.backlog} queued, waiting for a slot</div>
            )}
            <div className="mt-2">
              {identities.map((i) => (
                <div key={i.id} className="mb-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium">{i.displayName}</span>
                    <span className="text-content-dimmed">
                      {i.sentToday}/{i.dailyCap} · {i.status}
                    </span>
                  </div>
                  <div className="h-1.5 bg-surface-dimmed rounded mt-1 overflow-hidden">
                    <div
                      className="h-full bg-accent-1"
                      style={{ width: `${i.dailyCap ? Math.min(100, (i.sentToday / i.dailyCap) * 100) : 0}%` }}
                    />
                  </div>
                </div>
              ))}
              {identities.length === 0 && (
                <div className="text-xs text-content-dimmed">No sender identities yet. Add one to start.</div>
              )}
            </div>
          </>
        ) : (
          <div className="text-xs text-content-dimmed">Open the Email page to set up sending.</div>
        )}
      </div>
      <DropdownSeparator />
      <DropdownLinkItem path={paths.companyEmailPath()} icon={IconMail} title="Manage email" />
      <DropdownActionItem icon={IconQuestionMark} title="Keyboard shortcuts" onClick={onOpenKeyboardShortcuts} />
    </DropdownMenu>
  );
}
